import { useEffect } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

export const Lightbox = ({ photos, index, onClose, onIndex, labels }) => {
  const open = index !== null;
  const count = photos.length;
  const go = (d) => onIndex((index + d + count) % count);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onIndex((index + 1) % count);
      if (e.key === "ArrowLeft") onIndex((index - 1 + count) % count);
    };
    window.addEventListener("keydown", onKey);
    window.__lenis?.stop();
    return () => {
      window.removeEventListener("keydown", onKey);
      window.__lenis?.start();
    };
  }, [open, index, count, onClose, onIndex]);

  const photo = open ? photos[index] : null;
  const touch = { x: 0 };
  const onTouchStart = (e) => { touch.x = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touch.x;
    if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          key="lightbox"
          data-testid="gallery-lightbox"
          data-lenis-prevent
          role="dialog"
          aria-modal="true"
          aria-label={photo.caption}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={onClose}
          className="fixed inset-0 z-[70] flex flex-col items-center justify-center bg-[#040e0b]/95 backdrop-blur-xl px-4 py-16 sm:px-20"
        >
          <button data-testid="lightbox-close-btn" onClick={onClose} aria-label={labels.close} className="absolute top-4 right-4 sm:top-6 sm:right-6 grid h-12 w-12 place-items-center rounded-full border hairline bg-black/40 text-[var(--cream)] hover:bg-white/10 transition-colors">
            <X className="h-5 w-5" />
          </button>
          <div className="relative flex w-full max-w-5xl flex-1 min-h-0 items-center justify-center" onClick={(e) => e.stopPropagation()} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            <AnimatePresence initial={false}>
              <motion.img
                key={photo.src}
                data-testid="lightbox-image"
                src={photo.src}
                alt={photo.caption}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="absolute max-h-full max-w-full rounded-[20px] object-contain shadow-2xl"
              />
            </AnimatePresence>
          </div>
          <div className="mt-5 flex w-full max-w-5xl items-center justify-between gap-4" onClick={(e) => e.stopPropagation()}>
            <button data-testid="lightbox-prev-btn" onClick={() => go(-1)} aria-label={labels.prev} className="grid h-12 w-12 shrink-0 place-items-center rounded-full border hairline bg-black/40 text-[var(--cream)] hover:bg-white/10 transition-colors">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div className="text-center min-w-0">
              <p data-testid="lightbox-caption" className="font-display text-lg sm:text-2xl font-semibold text-[var(--cream)]">{photo.caption}</p>
              <p data-testid="lightbox-counter" className="font-mono text-xs text-[var(--sage)] mt-1">{index + 1} / {count}</p>
            </div>
            <button data-testid="lightbox-next-btn" onClick={() => go(1)} aria-label={labels.next} className="grid h-12 w-12 shrink-0 place-items-center rounded-full border hairline bg-black/40 text-[var(--cream)] hover:bg-white/10 transition-colors">
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};
